import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Card, Badge, Button, EmptyState, TextInput } from '@opendate-app/setlist';
import AdminPageHead from '../../AdminShared/AdminPageHead';
import client from '../../../shared/api/client';
import { toastError, errorText } from '../../../shared/toastHelper';
import '../styles/domainRules.css';

const BLANK = { domain: '', action: 'block', reason: '', team: null };

const MENU_PORTAL = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

const DomainRules = ({ rows, endpoints, actions }) => {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const actionOptions = actions.map((value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }));

  const openNew = () => { setEditingId('new'); setForm({ ...BLANK }); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      domain: row.domain || '',
      action: row.action || 'block',
      reason: row.reason || '',
      team: row.teamId ? { value: row.teamId, label: row.teamName || `Team #${row.teamId}` } : null,
    });
  };
  const cancel = () => { setEditingId(null); setForm(null); };

  const loadTeams = (input) =>
    client.get(endpoints.team_search, { params: { q: input } }).then((res) => res.data).catch(() => []);

  const teamOptionLabel = (option, { context }) => (
    context === 'menu' ? (
      <div>
        <div>{option.label}</div>
        <div className="dr-team-id">Team {option.value}</div>
      </div>
    ) : option.label
  );

  const save = async () => {
    setBusy(true);
    const payload = { domain_rule: { domain: form.domain, action: form.action, reason: form.reason, team_id: form.team ? form.team.value : null } };
    try {
      if (editingId === 'new') {
        await client.post(endpoints.base, payload);
      } else {
        await client.patch(`${endpoints.base}/${editingId}`, payload);
      }
      window.location.reload();
    } catch (e) {
      toastError(errorText(e));
      setBusy(false);
    }
  };

  const destroy = async (row) => {
    if (!window.confirm(`Delete the rule for ${row.domain}?`)) return;

    setBusy(true);
    try {
      await client.delete(`${endpoints.base}/${row.id}`);
      window.location.reload();
    } catch (e) {
      toastError(errorText(e));
      setBusy(false);
    }
  };

  const formRow = (
    <tr className="domain-rules-form-row">
      <td>
        <TextInput size="sm" placeholder="example.com" value={form?.domain || ''} onChange={(e) => setForm({ ...form, domain: e.target.value })} />
      </td>
      <td>
        <Select
          classNamePrefix="dr-select"
          options={actionOptions}
          value={actionOptions.find((o) => o.value === form?.action)}
          onChange={(o) => setForm({ ...form, action: o.value })}
          {...MENU_PORTAL}
        />
      </td>
      <td>
        <TextInput size="sm" placeholder="Reason (optional)" value={form?.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
      </td>
      <td>
        <AsyncSelect
          classNamePrefix="dr-select"
          isClearable
          placeholder="Platform-wide"
          cacheOptions
          defaultOptions={false}
          loadOptions={loadTeams}
          formatOptionLabel={teamOptionLabel}
          value={form?.team || null}
          onChange={(team) => setForm({ ...form, team })}
          {...MENU_PORTAL}
        />
      </td>
      <td />
      <td className="domain-rules-right domain-rules-nowrap">
        <div className="domain-rules-actions">
          <Button variant="secondary" onClick={cancel} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !form?.domain}>Save</Button>
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <AdminPageHead
        title="Domain Rules"
        subtitle="Block or allow email domains at checkout to stop fraud before it lands."
      >
        <Button variant="primary" onClick={openNew} disabled={editingId === 'new'}>New rule</Button>
      </AdminPageHead>
      <Card padding="none">
        <table className="domain-rules-table">
          <thead>
            <tr>
              <th scope="col">Domain</th>
              <th scope="col">Action</th>
              <th scope="col">Reason</th>
              <th scope="col">Team</th>
              <th scope="col">Added</th>
              <th scope="col" className="domain-rules-right" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {editingId === 'new' ? formRow : null}
            {rows.length === 0 && editingId !== 'new' ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState size="sm" title="No domain rules yet" description="Add a rule to block or allow an email domain." />
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              editingId === row.id ? (
                <React.Fragment key={row.key || row.id}>{formRow}</React.Fragment>
              ) : (
                <tr key={row.id} data-testid={`domain-rule-${row.id}`}>
                  <td className="domain-rules-domain">{row.domain}</td>
                  <td>
                    <Badge color={row.action === 'block' ? 'error' : 'success'}>{row.action}</Badge>
                  </td>
                  <td className="domain-rules-muted">{row.reason}</td>
                  <td className="domain-rules-muted">{row.teamName || 'Platform-wide'}</td>
                  <td className="domain-rules-muted domain-rules-nowrap">{row.createdAt}</td>
                  <td className="domain-rules-right domain-rules-nowrap">
                    <div className="domain-rules-actions">
                      <Button variant="secondary" onClick={() => openEdit(row)} disabled={busy || editingId !== null}>Edit</Button>
                      <Button variant="destructive" onClick={() => destroy(row)} disabled={busy || editingId !== null}>Delete</Button>
                    </div>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
};

DomainRules.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    domain: PropTypes.string,
    action: PropTypes.oneOf(['block', 'allow']),
    reason: PropTypes.string,
    teamId: PropTypes.number,
    teamName: PropTypes.string,
    createdAt: PropTypes.string,
  })),
  endpoints: PropTypes.shape({
    base: PropTypes.string.isRequired,
    team_search: PropTypes.string.isRequired,
  }).isRequired,
  actions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

DomainRules.defaultProps = { rows: [] };

export default DomainRules;
